import type { ExtendedDoc, ExtendedOp } from "../shared/types/worksheet";

import {
  clearLocalOpFieldTextTracker,
  enrichShareDBFailureLog,
  reportUncommittedWorksheetFieldTextLoss,
} from "./worksheet-field-content-client-log";

import { store } from "store/index";
import { selectCurrentSectionId, selectCurrentUserId } from "store/selectors";
import { logInfoWithDebugFlag, rollbarAndLogError } from "utils/logger";

/**
 * Submission tracking system for silent failure detection.
 *
 * This system tracks ShareDB operation submissions through their complete lifecycle
 * to detect silent failures where operations appear to succeed but don't actually
 * make it to the server..
 *
 * ## ShareDB Operation Lifecycle
 *
 * 1. **Submission**: User calls `submitOp()` on a ShareDB document
 * 2. **Queuing**: Operation is added to `pendingOps` queue (or composed into existing operation)
 * 3. **Local Application**: Operation is applied to local document for optimistic updates
 * 4. **Sending**: When no inflight operation exists, the operation is sent to server
 * 5. **Acknowledgment**: Server processes and sends back acknowledgment
 *
 * ## Multiple local submits before send (ShareDB "compose")
 *
 * Only one op is in flight at a time; extra `submitOp` calls sit in `pendingOps`. ShareDB may
 * **compose** them: merge successive local edits into one pending network message instead of
 * sending each separately. ShareDBDoc patches `Doc.prototype._tryCompose` so we get a definite
 * signal when that happens (`handleSubmissionComposed`).
 *
 * Each `submitOp` still gets a client `operationId`. After compose the merged id is cleared and
 * linked on the survivor tracker's `combinedOpIds` for field-loss reporting.
 *
 * ## Remote ops and queue reconciliation
 *
 * Doc `op` events from the server (`source === false`) only include the remote batch in
 * `ops[0]` — not our tracked `operationId`. Meanwhile ShareDB may OT-transform or drop
 * entries from `pendingOps` (e.g. concurrent edits on the same field).
 *
 * `reconcileSubmissionTrackersForDoc` runs after every doc op (local and remote) and
 * compares each active tracker to the current `pendingOps` + `inflightOp`. That way we
 * clear trackers when an op leaves the queue without a send/ack, instead of firing a
 * false "silent failure" timeout 60s later.
 *
 * ## Tracking Events
 *
 * - **"before op batch"**: Before operations are combined into a batch
 * - **"before op"**: Before operation is applied to local document
 * - **"op"**: After operation is applied to local document
 * - **"send"**: When operation is sent to server via connection
 * - **"receive"**: When server acknowledgment is received
 *
 * ## Silent Failure Detection
 *
 * We set a timeout on each operation. If no acknowledgment is received within this time
 * while the op is still in the send queue, we log detailed diagnostics.
 */
export interface SubmissionTracker {
  id: number;
  docId: string;
  operationId: string;
  combinedOpIds: string[];
  startTime: number;
  timeoutId: NodeJS.Timeout | null;
  currentStage: string;
  doc: ExtendedDoc;
  seq?: number;
  src?: string | null;
}

/** Why a per-submit tracker was cleared (for debug logs and timeout classification). */
export type TrackerClearCause =
  | "acked" // Server ack received (seq/src match in ShareDBConnection)
  | "composed" // ShareDB _tryCompose merged this submit into another pending batch
  | "removed-from-queue" // Gone from pending/inflight without send — usually OT no-op vs remote ops
  | "submit-error"
  | "rollback"
  | "timeout";

const SUBMISSION_TRACKING_TIMEOUT_MS = 60000; // 1 minute

export const submissionTrackers = {} as Record<string, SubmissionTracker>;
let nextSubmissionTrackerId = 1;

export const getOperationId = (op: ExtendedOp | null) => {
  if (!op) {
    return undefined;
  }

  if (op.operationId) {
    return op.operationId;
  }

  if (op.op) {
    return op.op.find((o) => o.operationId)?.operationId;
  }
};

/** All client `operationId`s currently waiting to send or in flight (not yet acked). */
export const collectOperationIdsFromQueue = (
  pendingOps: ExtendedOp[],
  inflightOp?: ExtendedOp | null,
): string[] => {
  const ids = new Set<string>();
  for (const pendingOp of pendingOps) {
    const id = getOperationId(pendingOp);
    if (id) {
      ids.add(id);
    }
  }
  if (inflightOp) {
    const inflightId = getOperationId(inflightOp);
    if (inflightId) {
      ids.add(inflightId);
    }
    inflightOp.op?.forEach((nestedOp) => {
      if (nestedOp.operationId) {
        ids.add(nestedOp.operationId);
      }
    });
  }
  return [...ids];
};

export const isOperationIdInQueue = (
  operationId: string,
  pendingOps: ExtendedOp[],
  inflightOp?: ExtendedOp | null,
): boolean => collectOperationIdsFromQueue(pendingOps, inflightOp).includes(operationId);

/** Attached to lifecycle debug logs and timeout Rollbar payloads. */
export const getSubmissionQueueSnapshot = (doc: ExtendedDoc) => ({
  docVersion: doc.version,
  pendingOpCount: doc.pendingOps.length,
  pendingOperationIds: collectOperationIdsFromQueue(doc.pendingOps),
  inflightOperationId: doc.inflightOp ? getOperationId(doc.inflightOp) : undefined,
  hasInflightOp: !!doc.inflightOp,
});

const logTrackerLifecycle = (
  doc: ExtendedDoc,
  message: string,
  payload: Record<string, unknown>,
) => {
  logInfoWithDebugFlag("debug_sharedb_silent_failures", message, {
    ...payload,
    ...getSubmissionQueueSnapshot(doc),
  });
};

const getTrackersForDoc = (docId: string): SubmissionTracker[] =>
  Object.values(submissionTrackers).filter((tracker) => tracker.docId === docId);

/** Keep field-text loss tied to the pending batch we still expect to send/ack. */
const addLostOperationIdToPendingBatchTracker = (
  lostOperationId: string,
  pendingBatchOperationId: string,
): void => {
  const pendingBatchTracker = getSubmissionTracker(pendingBatchOperationId);
  if (pendingBatchTracker && !pendingBatchTracker.combinedOpIds.includes(lostOperationId)) {
    pendingBatchTracker.combinedOpIds.push(lostOperationId);
  }
};

/** Called from ShareDBDoc's patched `_tryCompose` when ShareDB merges a submit into `pendingOps`. */
export const handleSubmissionComposed = (
  mergedOperationId: string,
  survivorOperationId: string,
  doc: ExtendedDoc,
): void => {
  const mergedTracker = submissionTrackers[mergedOperationId];
  if (!mergedTracker) {
    return;
  }

  logTrackerLifecycle(doc, "ShareDB composed submit into pending batch", {
    mergedOperationId,
    survivorOperationId,
  });
  addLostOperationIdToPendingBatchTracker(mergedOperationId, survivorOperationId);
  clearSubmissionTrackerById(mergedOperationId, {
    clearFieldTextTracker: false,
    cause: "composed",
  });
};

/** Our `operationId` is not in `pendingOps` or `inflightOp` — usually OT drop vs remote ops. */
const handleTrackerNoLongerInQueue = (
  tracker: SubmissionTracker,
  pendingOps: ExtendedOp[],
  stage: string,
): void => {
  // Sent over the wire; keep tracking until connection receive clears by seq/src.
  if (tracker.seq) {
    return;
  }

  const { doc } = tracker;
  if (isOperationIdInQueue(tracker.operationId, pendingOps, doc.inflightOp)) {
    return;
  }

  logTrackerLifecycle(doc, "Stopped tracking submit; no longer in send queue", {
    operationId: tracker.operationId,
    stage,
  });
  clearSubmissionTrackerById(tracker.operationId, { cause: "removed-from-queue" });
};

/**
 * Re-check every active tracker on this doc against the current send queue.
 * Call from ShareDBDoc on all `before op batch` / `before op` / `op` handlers so remote
 * applies (`source === false`) also update trackers — not only the local `ops[0]` id.
 */
export const reconcileSubmissionTrackersForDoc = (doc: ExtendedDoc, stage: string): void => {
  const { pendingOps } = doc;
  for (const tracker of getTrackersForDoc(doc.id)) {
    if (isOperationIdInQueue(tracker.operationId, pendingOps, doc.inflightOp)) {
      tracker.currentStage = stage;
    } else {
      handleTrackerNoLongerInQueue(tracker, pendingOps, stage);
    }
  }
};

export const createSubmissionTracker = (
  doc: ExtendedDoc,
  operationId: string,
): SubmissionTracker => {
  nextSubmissionTrackerId += 1;
  const tracker: SubmissionTracker = {
    id: nextSubmissionTrackerId,
    docId: doc.id,
    operationId,
    combinedOpIds: [],
    startTime: Date.now(),
    timeoutId: null,
    currentStage: "submitted",
    doc,
  };

  // Set timeout to track op submission
  tracker.timeoutId = setTimeout(() => {
    const age = Date.now() - tracker.startTime;
    const state = store.getState();
    const queueSnapshot = getSubmissionQueueSnapshot(doc);
    const stillInQueue = isOperationIdInQueue(tracker.operationId, doc.pendingOps, doc.inflightOp);
    // stuck-in-queue: likely real send/ack failure. orphan-tracker: reconcile should have cleared us.
    const timeoutKind = stillInQueue ? "stuck-in-queue" : "orphan-tracker";

    let fieldLosses: Record<string, unknown>[] = [];
    if (doc.data) {
      fieldLosses = reportUncommittedWorksheetFieldTextLoss({
        docId: tracker.docId,
        doc: doc.data,
        operationId: tracker.operationId,
        relatedOperationIds: tracker.combinedOpIds,
        cause: "timeout",
        userId: selectCurrentUserId(state),
        sectionId: selectCurrentSectionId(state),
        errorMessage: `No acknowledgment after ${age}ms (stage: ${tracker.currentStage}, kind: ${timeoutKind})`,
      });
    }

    rollbarAndLogError(
      stillInQueue
        ? "ShareDB operation timeout - op still in send queue"
        : "ShareDB operation timeout - tracker orphaned (queue reconcile missed)",
      enrichShareDBFailureLog(
        {
          trackerId: tracker.id,
          docId: tracker.docId,
          operationId: tracker.operationId,
          age,
          stage: tracker.currentStage,
          timeoutKind,
          stillInQueue,
          connectionState: doc.connection?.state,
          canSend: doc.connection?.canSend,
          seq: tracker.seq,
          src: tracker.src,
          paused: doc.paused,
          inflightOpAge: doc.inflightOp?.sentAt ? Date.now() - doc.inflightOp.sentAt : null,
          inflightOpRetries: doc.inflightOp?.retries || 0,
          ...queueSnapshot,
        },
        fieldLosses,
      ),
    );

    clearSubmissionTrackerById(operationId, { cause: "timeout" });
  }, SUBMISSION_TRACKING_TIMEOUT_MS);

  submissionTrackers[operationId] = tracker;
  return tracker;
};

export const updateSubmissionTracker = (
  operationId: string,
  pendingOps: ExtendedOp[],
  stage: string,
) => {
  const tracker = getSubmissionTracker(operationId);
  if (!tracker) {
    return;
  }

  if (isOperationIdInQueue(operationId, pendingOps, tracker.doc.inflightOp)) {
    tracker.currentStage = stage;
    return;
  }

  handleTrackerNoLongerInQueue(tracker, pendingOps, stage);
};

/** Inflight op id plus any operation ids composed into it (for field-text loss reporting). */
export const getRelatedOperationIds = (operationId: string | undefined): string[] => {
  if (!operationId) {
    return [];
  }
  const ids = new Set<string>([operationId]);
  const tracker = getSubmissionTracker(operationId);
  tracker?.combinedOpIds.forEach((combinedId) => ids.add(combinedId));
  return [...ids];
};

const setTrackerAcknowledgeInfo = (
  tracker: SubmissionTracker,
  seq: number,
  src: string | undefined | null,
) => {
  /* eslint-disable no-param-reassign */
  tracker.seq = seq;
  tracker.src = src;
  tracker.currentStage = "sent";
  /* eslint-enable no-param-reassign */
};

export const updateSubmissionTrackersAfterSend = (
  seq: number,
  src: string,
  newSrc?: string | null,
) => {
  const currentDoc = Object.values(submissionTrackers).find(
    (tkr) => tkr.doc.inflightOp?.seq === seq && tkr.doc.inflightOp?.src === src,
  )?.doc;
  if (!currentDoc) return;

  // When sharedb sends something from a submitOp it also sets
  // the inflightOp on the document.  We use this to continue tracking
  // the op as it should tell us which op was sent
  const inflightOp = currentDoc.inflightOp;

  const opIds: string[] = [];
  if (inflightOp?.operationId) {
    opIds.push(inflightOp.operationId);
  }

  if (inflightOp?.op) {
    inflightOp.op.forEach((op) => {
      if (op.operationId) {
        opIds.push(op.operationId);
      }
    });
  }

  opIds.forEach((opId) => {
    const tracker = getSubmissionTracker(opId);
    if (tracker) {
      logTrackerLifecycle(currentDoc, "Op submission has been sent", {
        operationId: opId,
        seq,
      });

      // We may not have a src yet so we take the connection id as it gets set after sending
      setTrackerAcknowledgeInfo(tracker, seq, src || newSrc);
    }
  });
};

export const clearSubmissionTrackers = (seq: number, src: string) => {
  const trackers = getTrackersBySeqAndSrc(seq, src);

  trackers.forEach((tracker) => {
    clearSubmissionTrackerById(tracker.operationId, { cause: "acked" });
  });
};

const clearFieldTextTrackersForSubmission = (
  tracker: SubmissionTracker | undefined,
  operationId: string,
): void => {
  clearLocalOpFieldTextTracker(operationId);
  tracker?.combinedOpIds.forEach((combinedId) => clearLocalOpFieldTextTracker(combinedId));
};

export const clearSubmissionTrackerById = (
  operationId: string,
  options?: { clearFieldTextTracker?: boolean; cause?: TrackerClearCause },
) => {
  // Direct map lookup only — composed merged ids alias to the survivor via getSubmissionTracker,
  // but clearing by merged id must not cancel the survivor's timeout.
  const tracker = submissionTrackers[operationId];

  if (!tracker) {
    if (options?.clearFieldTextTracker !== false) {
      clearLocalOpFieldTextTracker(operationId);
    }
    return;
  }

  logTrackerLifecycle(tracker.doc, "Clearing tracker", {
    operationId,
    cause: options?.cause,
    seq: tracker.seq,
    stage: tracker.currentStage,
  });

  if (tracker.timeoutId) {
    clearTimeout(tracker.timeoutId);
  }

  delete submissionTrackers[operationId];
  if (options?.clearFieldTextTracker !== false) {
    clearFieldTextTrackersForSubmission(tracker, operationId);
  }
};

export const getSubmissionTracker = (operationId: string): SubmissionTracker | undefined => {
  let tracker: SubmissionTracker | undefined = submissionTrackers[operationId];

  // After compose the merged id is cleared but listed on the survivor tracker's combinedOpIds.
  if (!tracker) {
    const trackers = getSubmissionTrackers();
    tracker = trackers.find((tkr) => tkr.combinedOpIds.includes(operationId));
  }

  return tracker;
};

export const getSubmissionTrackers = (): SubmissionTracker[] => Object.values(submissionTrackers);

const getTrackersBySeqAndSrc = (seq: number, src: string | null): SubmissionTracker[] => {
  const trackers = getSubmissionTrackers();

  return trackers.filter((tkr) => tkr.seq === seq && tkr.src === src);
};
